/** external */
import _ from "lodash";
import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

/** internal */
import { useI18n } from "Hooks";
import { isMutationAndQueriesV2Enabled } from "Worksheet/modules/WorksheetUtils";
import {
  isWorksheetMutationsV2Enabled,
  getIsLearningGoalsRevampEnabled,
} from "Worksheet/modules/WorksheetFeatureFlags";
import {
  ORG_RESOURCE_TYPE_WORKSHEET,
  RESOURCE_TYPE_QUESTION,
  DYNAMIC_FIELDS,
  MODE_TEACHER_CREATE,
  WORKSHEET_MODES,
  FIELD_UID_ADD_LEARNING_GOALS,
  FIELD_UID_ASSESSMENT_TOOL,
  REQUIRED_FIELD_FOR_ASSESSMENT_TOOL,
} from "Worksheet/modules/WorksheetConstants";
import {
  createQuestion,
  createQuestionField,
  updateActiveQuestionField,
  createQuestionV2,
  createQuestionFieldV2,
} from "Worksheet/modules/WorksheetModules";
import {
  createQuestionLibrarySubQuestion,
  createLibraryQuestionField,
  createLibraryQuestionFieldV2,
  createQuestionLibrarySubQuestionV2,
} from "QuestionBank/modules/QuestionBankModule";
import { ORG_RESOURCE_TYPE_QUESTION_BANK } from "QuestionBank/modules/Constants";

/** relative */
import AddCustomFields from "./AddCustomFields";
import DynamicFieldsViewV2 from "./DynamicFieldsViewV2";
import AddQuestionsDropdownV2 from "../../AddQuestionsDropdownV2";

const DynamicFields = props => {
  const {
    dynamicFields,
    updateFields,
    isActiveQuestion,
    mode,
    questionId,
    assessmentTools,
    showAddQuestionDropdown,
    createQuestion,
    createQuestionField,
    resourceId,
    addButtonConfig,
    isSubQuestionSelected,
    showDynamicFields,
    putAfterItemId,
    updateActiveQuestionField,
    createQuestionLibrarySubQuestion,
    isFromInternalTool,
    createLibraryQuestionField,
    createLibraryQuestionFieldV2,
    portalType,
    learningGoalsDropdownOptions,
    learningGoalsFields,
    learningGoalsMapping,
    getLearningGoalsTagCountTooltip,
    isSubQuestion,
    fieldList,
    questionFields,
    createQuestionV2,
    isQueriesV2Enabled,
    createQuestionFieldV2,
    unitPlanTemplate,
    createQuestionLibrarySubQuestionV2,
  } = props;

  const isTeacherCreateMode = mode === MODE_TEACHER_CREATE;
  const [didMount, setDidMount] = useState(false);
  const dynamicFieldRefs = useRef({});
  const { t } = useI18n(["common"]);

  const addQuestion = async ({ key }) => {
    if (_.includes(DYNAMIC_FIELDS, key)) {
      await addNewQuestionField({ key });
    } else {
      if (isFromInternalTool) {
        if (isMutationAndQueriesV2Enabled()) {
          await createQuestionLibrarySubQuestionV2({
            questionTypeId: key,
            putAfterItemId,
          });
        } else {
          await createQuestionLibrarySubQuestion({
            parentResourceId: resourceId,
            questionTypeId: key,
            organizationResources: [
              {
                type: ORG_RESOURCE_TYPE_QUESTION_BANK,
                resourceType: RESOURCE_TYPE_QUESTION,
              },
            ],
            putAfterItemId,
          });
        }
      } else {
        if (isWorksheetMutationsV2Enabled()) {
          await createQuestionV2({
            parentId: questionId,
            parentType: "QUESTION",
            questionTypeId: key,
            putAfterQuestionId: putAfterItemId,
            isQueriesV2Enabled,
          });
        } else {
          await createQuestion({
            parentResourceId: resourceId,
            questionTypeId: key,
            organizationResources: [
              {
                type: ORG_RESOURCE_TYPE_WORKSHEET,
                resourceType: RESOURCE_TYPE_QUESTION,
              },
            ],
            putAfterItemId,
          });
        }
      }
    }
  };

  //WORKSHEET_RESTRUCTURING:Remove createQuestionField when queriesV2 is enabled
  const _createQuestionField = isQueriesV2Enabled
    ? createQuestionFieldV2
    : createQuestionField;

  const addNewQuestionField = async ({ key }) => {
    setDidMount(true);
    if (isFromInternalTool) {
      if (isMutationAndQueriesV2Enabled()) {
        await createLibraryQuestionFieldV2({ uid: key, questionId });
      } else {
        await createLibraryQuestionField({ uid: key, questionId });
      }
    } else {
      await _createQuestionField({ uids: [key], portalType, questionId });
    }
    updateActiveQuestionField(key);
  };

  const handleLearningGoalsDropdownClick = async option => {
    const { key } = option;
    const fieldData = _.find(dynamicFields, { uid: key });

    const isLearningGoalsRevampField = getIsLearningGoalsRevampEnabled();

    // Don't create question fields for revamped dropdown options
    // The fields are created upon selecting and saving learning goals from the modal
    if (_.isEmpty(fieldData) && !isLearningGoalsRevampField) {
      await _createQuestionField({ uids: [key], portalType, questionId });
    }

    dynamicFieldRefs?.current?.[
      `${questionId}-${FIELD_UID_ADD_LEARNING_GOALS}`
    ]?.onDropdownItemClick?.(option);
  };

  const handleAssessmentToolDropdownClick = async option => {
    const selectedKey = _.get(option, "key");

    // this fields needs to be created in questionFields for adding assessment tool
    const requiredFieldUID = REQUIRED_FIELD_FOR_ASSESSMENT_TOOL[selectedKey];
    // if required field not exist in questionFields then create it
    if (!_.isEmpty(requiredFieldUID)) {
      const fieldData = _.find(questionFields, { uid: requiredFieldUID });

      if (_.isEmpty(fieldData)) {
        await _createQuestionField({
          uids: [requiredFieldUID],
          portalType,
          questionId,
        });
      }
    }

    const onChangeDropDownMenuOption = _.get(dynamicFieldRefs, [
      "current",
      `${questionId}-${FIELD_UID_ASSESSMENT_TOOL}`,
      "onChangeDropDownMenuOption",
    ]);

    if (_.isFunction(onChangeDropDownMenuOption))
      onChangeDropDownMenuOption(option);
  };

  const showCustomFields = isTeacherCreateMode && isActiveQuestion;
  const showQuestionsDropdown = showAddQuestionDropdown && showCustomFields;

  return (
    <>
      {!_.isEmpty(dynamicFields) ? (
        <DynamicFieldsViewV2
          data={dynamicFields}
          updateFields={updateFields}
          isActiveQuestion={isActiveQuestion}
          questionId={questionId}
          assessmentTools={assessmentTools}
          mode={mode}
          didMount={didMount}
          setDidMount={setDidMount}
          showDynamicFields={showDynamicFields}
          isSubQuestionSelected={isSubQuestionSelected}
          isFromInternalTool={isFromInternalTool}
          portalType={portalType}
          learningGoalsDropdownOptions={learningGoalsDropdownOptions}
          learningGoalsFields={learningGoalsFields}
          learningGoalsMapping={learningGoalsMapping}
          getLearningGoalsTagCountTooltip={getLearningGoalsTagCountTooltip}
          dynamicFieldRefs={dynamicFieldRefs}
          handleLearningGoalsDropdownClick={handleLearningGoalsDropdownClick}
          fieldList={fieldList}
          handleAssessmentToolDropdownClick={handleAssessmentToolDropdownClick}
          questionFields={questionFields}
          isSubQuestion={isSubQuestion}
          onOptionClick={addNewQuestionField}
          unitPlanTemplate={unitPlanTemplate}
        />
      ) : null}

      {showQuestionsDropdown ? (
        <AddQuestionsDropdownV2
          onOptionClick={addQuestion}
          parentResourceId={resourceId}
          putAfterItemId={putAfterItemId}
          isButtonRounded={true}
          showAddInstructions={true}
          isFromInternalTool={isFromInternalTool}
          parentItemId={questionId}
          {...addButtonConfig}
          isQueriesV2Enabled={isQueriesV2Enabled}
        />
      ) : null}

      {showCustomFields ? (
        <AddCustomFields
          dynamicFields={dynamicFields}
          t={t}
          onOptionClick={addNewQuestionField}
          isSubQuestion={isSubQuestion}
          showAddQuestionDropdown={showAddQuestionDropdown}
          data={dynamicFields}
          updateFields={updateFields}
          isActiveQuestion={isActiveQuestion}
          questionId={questionId}
          assessmentTools={assessmentTools}
          mode={mode}
          showDynamicFields={showDynamicFields}
          isSubQuestionSelected={isSubQuestionSelected}
          isFromInternalTool={isFromInternalTool}
          portalType={portalType}
          learningGoalsDropdownOptions={learningGoalsDropdownOptions}
          learningGoalsFields={learningGoalsFields}
          learningGoalsMapping={learningGoalsMapping}
          getLearningGoalsTagCountTooltip={getLearningGoalsTagCountTooltip}
          dynamicFieldRefs={dynamicFieldRefs}
          handleLearningGoalsDropdownClick={handleLearningGoalsDropdownClick}
          handleAssessmentToolDropdownClick={handleAssessmentToolDropdownClick}
          questionFields={questionFields}
          fieldList={fieldList}
          unitPlanTemplate={unitPlanTemplate}
        />
      ) : null}
    </>
  );
};

DynamicFields.propTypes = {
  dynamicFields: PropTypes.array,
  updateFields: PropTypes.func,
  isActiveQuestion: PropTypes.bool,
  showAddQuestionDropdown: PropTypes.bool,
  resourceId: PropTypes.string,
  addButtonConfig: PropTypes.object,
  mode: PropTypes.oneOf(WORKSHEET_MODES),
  showDynamicFields: PropTypes.bool,
  isSubQuestionSelected: PropTypes.bool,
  createLibraryQuestionField: PropTypes.func,
  createLibraryQuestionFieldV2: PropTypes.func,
  createQuestionLibrarySubQuestion: PropTypes.func,
  isFromInternalTool: PropTypes.bool,
  learningGoalsDropdownOptions: PropTypes.arrayOf(PropTypes.object),
  learningGoalsFields: PropTypes.objectOf(PropTypes.object),
  isSubQuestion: PropTypes.bool,
  fieldList: PropTypes.object,
  questionFields: PropTypes.arrayOf(PropTypes.object),
  isQueriesV2Enabled: PropTypes.bool,
  createQuestionLibrarySubQuestionV2: PropTypes.func,
};

DynamicFields.defaultProps = {
  showAddQuestionDropdown: false,
  addButtonConfig: {},
  isSubQuestionSelected: false,
  learningGoalsDropdownOptions: [],
  learningGoalsFields: {},
  isSubQuestion: false,
  fieldList: {},
  questionFields: [],
  isQueriesV2Enabled: false,
  createQuestionLibrarySubQuestionV2: _.noop,
  createLibraryQuestionFieldV2: _.noop,
};

const mapActionCreators = {
  createQuestion,
  createQuestionField,
  updateActiveQuestionField,
  createQuestionLibrarySubQuestion,
  createLibraryQuestionField,
  createLibraryQuestionFieldV2,
  createQuestionV2,
  createQuestionFieldV2,
  createQuestionLibrarySubQuestionV2,
};

export default connect(null, mapActionCreators)(DynamicFields);
